import { useState } from 'react'
import {
  LayoutDashboard, Monitor, Package, ClipboardList, Calendar,
  FileText, Settings, BookOpen, ChevronRight, Search,
  Download, Trash2, Edit2, Printer, Paperclip,
  Database, Users, Filter, BarChart2, ShieldCheck,
  KeyRound, LogIn, Copy, Link2,
} from 'lucide-react'

interface Section {
  id: string
  label: string
  icon: React.ElementType
}

const SECTIONS: Section[] = [
  { id: 'overview',   label: 'ภาพรวมระบบ',       icon: BookOpen       },
  { id: 'dashboard',  label: 'Dashboard',          icon: LayoutDashboard },
  { id: 'assets',     label: 'Assets (ครุภัณฑ์)',  icon: Monitor        },
  { id: 'software',   label: 'Software & License', icon: Package        },
  { id: 'deliveries', label: 'ใบส่งมอบ',           icon: ClipboardList  },
  { id: 'bookings',   label: 'การยืม',             icon: Calendar       },
  { id: 'reports',    label: 'รายงาน',             icon: FileText       },
  { id: 'settings',   label: 'ตั้งค่าระบบ',        icon: Settings       },
  { id: 'sso',        label: 'SAML SSO',           icon: KeyRound       },
  { id: 'roles',      label: 'สิทธิ์การใช้งาน',   icon: ShieldCheck    },
]

function Tag({ color, children }: { color: string; children: React.ReactNode }) {
  const cls: Record<string, string> = {
    blue:   'bg-blue-50 text-blue-700 border-blue-200',
    green:  'bg-green-50 text-green-700 border-green-200',
    amber:  'bg-amber-50 text-amber-700 border-amber-200',
    red:    'bg-red-50 text-red-700 border-red-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    gray:   'bg-gray-100 text-gray-600 border-gray-200',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${cls[color] || cls.gray}`}>
      {children}
    </span>
  )
}

function SectionTitle({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle?: string }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon size={17} className="text-white" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  )
}

function StepList({ steps }: { steps: string[] }) {
  return (
    <ol className="space-y-2 mt-3">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-sm text-gray-700">
          <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-semibold">
            {i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  )
}

function InfoBox({ title, children, color = 'blue' }: { title: string; children: React.ReactNode; color?: string }) {
  const cls: Record<string, string> = {
    blue:  'border-blue-200 bg-blue-50',
    amber: 'border-amber-200 bg-amber-50',
    green: 'border-green-200 bg-green-50',
  }
  const titleCls: Record<string, string> = {
    blue:  'text-blue-800',
    amber: 'text-amber-800',
    green: 'text-green-800',
  }
  return (
    <div className={`rounded-xl border p-4 mt-4 ${cls[color] || cls.blue}`}>
      <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${titleCls[color] || titleCls.blue}`}>{title}</p>
      <div className="text-sm text-gray-700 space-y-1">{children}</div>
    </div>
  )
}

function FeatureRow({ icon: Icon, label, desc }: { icon: React.ElementType; label: string; desc: string }) {
  return (
    <div className="flex gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon size={14} className="text-gray-600" />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
      </div>
    </div>
  )
}

// ─── Section Contents ─────────────────────────────────────────────────────────

function OverviewSection() {
  return (
    <div>
      <SectionTitle icon={BookOpen} title="ภาพรวมระบบ" subtitle="IT Management System — คู่มือการใช้งาน" />
      <p className="text-sm text-gray-700 leading-relaxed">
        ระบบ IT Management ช่วยให้ทีม IT สามารถบริหารจัดการครุภัณฑ์และอุปกรณ์ไอที ติดตามสถานะ
        จัดการใบส่งมอบ ตรวจสอบ Software License และสร้างรายงานได้ในที่เดียว
      </p>
      <div className="grid grid-cols-2 gap-3 mt-5">
        {[
          { icon: Monitor,       label: 'Assets',         desc: 'บริหารครุภัณฑ์ทั้งหมด'           },
          { icon: Package,       label: 'Software',       desc: 'ติดตาม License ซอฟต์แวร์'       },
          { icon: ClipboardList, label: 'ใบส่งมอบ',       desc: 'เอกสารการมอบ-รับอุปกรณ์'       },
          { icon: Calendar,      label: 'การยืม',         desc: 'ยืมอุปกรณ์ใช้งานชั่วคราว'      },
          { icon: FileText,      label: 'รายงาน',         desc: 'วิเคราะห์และ Export ข้อมูล'    },
          { icon: Settings,      label: 'ตั้งค่า',        desc: 'Master Data และ Azure Sync'    },
        ].map(({ icon: Icon, label, desc }) => (
          <div key={label} className="bg-gray-50 rounded-xl p-3 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
              <Icon size={15} className="text-gray-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{label}</p>
              <p className="text-xs text-gray-500">{desc}</p>
            </div>
          </div>
        ))}
      </div>
      <InfoBox title="สิทธิ์การใช้งาน" color="amber">
        <p>ระบบแบ่งผู้ใช้เป็น 2 ระดับ — <strong>ADMIN</strong> มีสิทธิ์เต็ม (เพิ่ม/แก้ไข/ลบ/อนุมัติ) และ <strong>USER</strong> มีสิทธิ์ดูและสร้างรายการยืมเท่านั้น</p>
      </InfoBox>
    </div>
  )
}

function DashboardSection() {
  return (
    <div>
      <SectionTitle icon={LayoutDashboard} title="Dashboard" subtitle="ภาพรวมสถิติและสถานะของอุปกรณ์ทั้งหมด" />
      <p className="text-sm text-gray-700 leading-relaxed">
        Dashboard แสดงข้อมูลสรุปในรูปแบบกราฟและ Card เพื่อให้เห็นภาพรวมของ Asset ทั้งหมดได้อย่างรวดเร็ว
      </p>
      <div className="mt-4 space-y-0">
        <FeatureRow icon={BarChart2} label="สถิติ Asset" desc="จำนวน Asset แยกตามประเภท สถานะ และมูลค่ารวม" />
        <FeatureRow icon={Filter} label="Filter ตาม Organization" desc="กรองข้อมูลตาม Company → Site → Department ได้แบบ Cascade" />
        <FeatureRow icon={Monitor} label="กราฟแยกประเภท" desc="Pie Chart และ Bar Chart แสดงการกระจายตัวของ Asset" />
        <FeatureRow icon={ChevronRight} label="Drill-down" desc="คลิก Card หรือ Chart เพื่อไปดูรายการ Asset ที่ตรงกัน" />
      </div>
      <InfoBox title="การใช้งาน Filter" color="blue">
        <p>• เลือก <strong>บริษัท</strong> ก่อน จากนั้น <strong>Site</strong> และ <strong>Department</strong> จะ Filter ตามลำดับโดยอัตโนมัติ</p>
        <p>• ข้อมูลที่แสดงบน Dashboard จะเปลี่ยนตาม Filter ที่เลือก</p>
      </InfoBox>
    </div>
  )
}

function AssetsSection() {
  return (
    <div>
      <SectionTitle icon={Monitor} title="Assets (ครุภัณฑ์)" subtitle="จัดการอุปกรณ์และครุภัณฑ์ไอทีทั้งหมด" />

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-1">การค้นหาและกรองข้อมูล</h3>
      <div className="space-y-0">
        <FeatureRow icon={Search} label="ค้นหา" desc="พิมพ์ Asset Code, ชื่อ, Serial Number หรือ IP Address ในช่องค้นหา" />
        <FeatureRow icon={Filter} label="Filter" desc="กรองตามประเภท, สถานะ, บริษัท, Site และ Department ได้พร้อมกัน" />
        <FeatureRow icon={ChevronRight} label="เรียงลำดับ" desc="คลิกหัวตารางเพื่อ Sort ข้อมูล คลิกซ้ำเพื่อสลับทิศทาง" />
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">การจัดการ Asset (สำหรับ Admin)</h3>
      <StepList steps={[
        'คลิกปุ่ม "+ เพิ่ม Asset" มุมขวาบน',
        'กรอกข้อมูลในฟอร์ม — Asset Code, ประเภท, บริษัท, Site, Department, สถานะ',
        'กรอกข้อมูลเพิ่มเติมได้ เช่น Serial Number, สเปค, IP, ราคา, วันหมดประกัน',
        'คลิก "บันทึก" เพื่อสร้าง Asset ใหม่',
      ]} />

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">ฟีเจอร์อื่นๆ</h3>
      <div className="space-y-0">
        <FeatureRow icon={Download} label="Export Excel" desc="คลิกปุ่ม Export เพื่อดาวน์โหลดรายการ Asset ทั้งหมดเป็นไฟล์ .xlsx" />
        <FeatureRow icon={Trash2} label="ลบแบบ Bulk" desc="เลือก Asset หลายรายการด้วย Checkbox แล้วคลิก 'ลบที่เลือก'" />
        <FeatureRow icon={Printer} label="พิมพ์ใบส่งมอบ" desc="จากรายการ Asset คลิกไอคอนพิมพ์เพื่อเปิดใบส่งมอบของ Asset นั้น" />
        <FeatureRow icon={Paperclip} label="แนบไฟล์" desc="คลิกไอคอน Paperclip เพื่อแนบไฟล์ PDF หรือรูปภาพกับใบส่งมอบ" />
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">สถานะ Asset</h3>
      <div className="flex flex-wrap gap-2 mt-2">
        <Tag color="green">ใช้งานอยู่</Tag>
        <Tag color="blue">พร้อมใช้</Tag>
        <Tag color="amber">ซ่อมบำรุง</Tag>
        <Tag color="red">เสีย / ปลดระวาง</Tag>
        <Tag color="gray">สำรอง</Tag>
      </div>
      <InfoBox title="หมายเหตุ" color="amber">
        <p>Asset ที่มี <strong>ผู้ใช้งาน (Assigned To)</strong> แต่ยังไม่มีใบส่งมอบ จะแสดง badge "ไม่มีใบส่งมอบ" สีเหลือง — ควรสร้างใบส่งมอบเพื่อความครบถ้วนของเอกสาร</p>
      </InfoBox>
    </div>
  )
}

function SoftwareSection() {
  return (
    <div>
      <SectionTitle icon={Package} title="Software & License" subtitle="จัดการ Software License และการผูก Seat" />
      <p className="text-sm text-gray-700 leading-relaxed">
        หน้านี้ใช้บริหาร Software License ขององค์กร ติดตามจำนวน Seat ที่ใช้งาน และวันหมดอายุ
      </p>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">ประเภท License</h3>
      <div className="space-y-2 mt-2">
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <Monitor size={14} className="text-blue-600" />
            <span className="text-sm font-semibold text-gray-800">Asset Bound</span>
            <Tag color="blue">ASSET_BOUND</Tag>
          </div>
          <p className="text-xs text-gray-600">License ผูกกับเครื่อง เช่น Windows OS — ผูกได้กับ Asset (เครื่องคอมพิวเตอร์)</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <Users size={14} className="text-purple-600" />
            <span className="text-sm font-semibold text-gray-800">User Bound</span>
            <Tag color="purple">USER_BOUND</Tag>
          </div>
          <p className="text-xs text-gray-600">License ผูกกับผู้ใช้ เช่น Microsoft 365 — ผูกได้กับ User ในระบบ</p>
        </div>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">การเพิ่ม License</h3>
      <StepList steps={[
        'คลิกปุ่ม "+ เพิ่ม License"',
        'กรอกชื่อซอฟต์แวร์, เวอร์ชัน, ประเภท (Asset/User Bound), หมวดหมู่ (OS หรือ General)',
        'ระบุจำนวน Seat (เว้นว่างหากไม่จำกัด), วันหมดอายุ, บริษัท และ License Key',
        'คลิก "บันทึก"',
      ]} />

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">การผูก Seat</h3>
      <StepList steps={[
        'คลิกปุ่ม "ผูก Seat" บน License Card',
        'ค้นหาและเลือก Asset หรือ User ที่ต้องการ',
        'กรอก Notes หากจำเป็น แล้วคลิก "ผูก"',
        'ยกเลิกการผูกได้โดยคลิก "ยกเลิก" ในรายการ Assignment',
      ]} />

      <InfoBox title="การแจ้งเตือน" color="amber">
        <p>• License ที่ <strong>หมดอายุแล้ว</strong> จะแสดงวันที่สีแดง</p>
        <p>• License ที่จะหมดอายุ <strong>ภายใน 30 วัน</strong> จะแสดงวันที่สีเหลือง</p>
        <p>• Seat ที่เต็มแล้วจะไม่สามารถผูกเพิ่มได้</p>
      </InfoBox>
    </div>
  )
}

function DeliveriesSection() {
  return (
    <div>
      <SectionTitle icon={ClipboardList} title="ใบส่งมอบ" subtitle="จัดการเอกสารการส่งมอบและรับคืนอุปกรณ์" />
      <p className="text-sm text-gray-700 leading-relaxed">
        ใบส่งมอบคือเอกสารบันทึกการมอบอุปกรณ์ให้กับพนักงาน ใช้เป็นหลักฐานการครอบครองอุปกรณ์
      </p>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">การสร้างใบส่งมอบ</h3>
      <StepList steps={[
        'คลิกปุ่ม "+ สร้างใบส่งมอบ"',
        'เลือก Asset ที่ต้องการส่งมอบ',
        'กรอกชื่อพนักงาน, รหัสพนักงาน, แผนก, ที่ตั้ง',
        'กรอกวันที่เริ่มสัญญา / สิ้นสุดสัญญา (ถ้ามี)',
        'คลิก "บันทึก" — ระบบจะสร้างเลขที่ใบส่งมอบอัตโนมัติ',
      ]} />

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">ฟีเจอร์</h3>
      <div className="space-y-0">
        <FeatureRow icon={Printer} label="พิมพ์ใบส่งมอบ" desc="คลิกไอคอนพิมพ์เพื่อเปิดหน้า Preview สำหรับพิมพ์หรือบันทึก PDF" />
        <FeatureRow icon={Paperclip} label="แนบไฟล์สแกน" desc="แนบไฟล์ PDF หรือรูปภาพ (JPG, PNG) ของใบส่งมอบที่เซ็นแล้ว รองรับ Drag & Drop" />
        <FeatureRow icon={Edit2} label="แก้ไขใบส่งมอบ" desc="คลิกปุ่มดินสอเพื่อแก้ไขข้อมูลในใบส่งมอบ" />
        <FeatureRow icon={Trash2} label="ลบใบส่งมอบ" desc="ลบใบส่งมอบออกจากระบบ (เฉพาะ Admin)" />
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">สถานะใบส่งมอบ</h3>
      <div className="space-y-2 mt-2">
        {[
          { tag: 'รอแนบไฟล์',  color: 'amber' as const, desc: 'สร้างแล้วแต่ยังไม่มีไฟล์สแกนแนบ' },
          { tag: 'มีใบส่งมอบ', color: 'green' as const, desc: 'มีไฟล์สแกนใบส่งมอบที่เซ็นแล้ว'  },
          { tag: 'ยกเลิก',     color: 'red'   as const, desc: 'ใบส่งมอบถูกยกเลิก'              },
        ].map(({ tag, color, desc }) => (
          <div key={tag} className="flex items-center gap-3">
            <Tag color={color}>{tag}</Tag>
            <span className="text-xs text-gray-500">{desc}</span>
          </div>
        ))}
      </div>
      <InfoBox title="การค้นหา" color="blue">
        <p>ค้นหาได้ด้วยเลขที่ใบส่งมอบ, ชื่อพนักงาน, แผนก, Asset Code หรือ Serial Number</p>
      </InfoBox>
    </div>
  )
}

function BookingsSection() {
  return (
    <div>
      <SectionTitle icon={Calendar} title="การยืม" subtitle="ยืมและบริหารการใช้งานอุปกรณ์ชั่วคราว" />
      <p className="text-sm text-gray-700 leading-relaxed">
        ระบบการยืมช่วยให้ผู้ใช้สามารถขอยืมอุปกรณ์เพื่อใช้งานในช่วงเวลาที่กำหนด และ Admin สามารถอนุมัติหรือปฏิเสธได้
      </p>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">การสร้างรายการยืม</h3>
      <StepList steps={[
        'คลิกปุ่ม "+ ยืม Asset"',
        'เลือก Asset ที่ต้องการยืม',
        'เลือกวันที่ยืมและวันคืน (กำหนด)',
        'กรอกวัตถุประสงค์การใช้งาน, ชื่อผู้ยืม และ Notes',
        'คลิก "ยืนยันการยืม" — ระบบจะตรวจสอบ Conflict อัตโนมัติ',
      ]} />

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">การดูตาราง</h3>
      <div className="space-y-0">
        <FeatureRow icon={ClipboardList} label="มุมมองรายการ" desc="แสดงรายการยืมทั้งหมดในรูปแบบตาราง สามารถกรองตามเดือนได้" />
        <FeatureRow icon={Calendar} label="มุมมองปฏิทิน" desc="แสดงการยืมบนปฏิทินรายเดือน เห็นภาพรวมการใช้งาน" />
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">สถานะการยืม</h3>
      <div className="space-y-2 mt-2">
        {[
          { tag: 'รอดำเนินการ', color: 'amber'  as const, desc: 'รอ Admin อนุมัติ'             },
          { tag: 'กำลังยืม',    color: 'green'  as const, desc: 'อนุมัติแล้ว กำลังใช้งาน'    },
          { tag: 'ปฏิเสธ',      color: 'red'    as const, desc: 'Admin ปฏิเสธรายการยืม'       },
          { tag: 'คืนแล้ว',     color: 'blue'   as const, desc: 'คืนอุปกรณ์เรียบร้อยแล้ว'   },
          { tag: 'ยกเลิก',      color: 'gray'   as const, desc: 'ผู้ยืมยกเลิกเอง'            },
        ].map(({ tag, color, desc }) => (
          <div key={tag} className="flex items-center gap-3">
            <Tag color={color}>{tag}</Tag>
            <span className="text-xs text-gray-500">{desc}</span>
          </div>
        ))}
      </div>
      <InfoBox title="สำหรับ Admin" color="blue">
        <p>Admin สามารถ <strong>อนุมัติ</strong> หรือ <strong>ปฏิเสธ</strong> รายการยืมได้จากรายการ และเปลี่ยนสถานะเป็น "คืนแล้ว" เมื่ออุปกรณ์ถูกส่งคืน</p>
      </InfoBox>
    </div>
  )
}

function ReportsSection() {
  return (
    <div>
      <SectionTitle icon={FileText} title="รายงาน" subtitle="วิเคราะห์และ Export ข้อมูล Asset" />
      <p className="text-sm text-gray-700 leading-relaxed">
        หน้ารายงานรวบรวมการวิเคราะห์ข้อมูล Asset หลายรูปแบบ สามารถ Export เป็น CSV ได้ทุกรายงาน
      </p>

      <div className="space-y-3 mt-4">
        {[
          {
            title: 'Asset Inventory',
            desc: 'สรุปจำนวน Asset แยกตามประเภทและสถานะ พร้อมกราฟแสดงสัดส่วน',
          },
          {
            title: 'Expiry / Warranty',
            desc: 'รายการ Asset ที่ประกันหมดอายุแล้ว หรือจะหมดอายุในอีก 90 วัน',
          },
          {
            title: 'Asset Value',
            desc: 'มูลค่า Asset แยกตามประเภท ราคาซื้อ ค่าเสื่อมราคา และมูลค่าตามบัญชี (Net Book)',
          },
          {
            title: 'Overdue MA',
            desc: 'รายการ Asset ที่ถึงกำหนดซ่อมบำรุงแต่ยังไม่ได้ดำเนินการ',
          },
          {
            title: 'By Department',
            desc: 'สรุปจำนวนและมูลค่า Asset แยกตามหน่วยงาน',
          },
        ].map(r => (
          <div key={r.title} className="bg-gray-50 rounded-xl p-3.5">
            <p className="text-sm font-semibold text-gray-800">{r.title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{r.desc}</p>
          </div>
        ))}
      </div>

      <InfoBox title="การ Export" color="green">
        <p>• คลิกปุ่ม <strong>Export CSV</strong> ที่มุมขวาบนของแต่ละรายงาน</p>
        <p>• ไฟล์ CSV รองรับ UTF-8 BOM สำหรับเปิดใน Excel ได้ถูกต้อง</p>
        <p>• กรองด้วย <strong>Company</strong> ก่อน Export เพื่อรับข้อมูลเฉพาะบริษัทที่ต้องการ</p>
      </InfoBox>
    </div>
  )
}

function SettingsSection() {
  return (
    <div>
      <SectionTitle icon={Settings} title="ตั้งค่าระบบ" subtitle="จัดการ Master Data, Import และการเชื่อมต่อ (เฉพาะ Admin)" />

      <h3 className="text-sm font-semibold text-gray-700 mb-3 mt-1">แท็บ Master Data</h3>
      <div className="space-y-0">
        <FeatureRow icon={Database} label="บริษัท / Company" desc="เพิ่ม/แก้ไขข้อมูลบริษัทในองค์กร เป็นจุดเริ่มต้นของโครงสร้าง" />
        <FeatureRow icon={Database} label="Site" desc="สาขาหรือพื้นที่ภายใต้บริษัท" />
        <FeatureRow icon={Database} label="Department" desc="หน่วยงาน/แผนกภายใต้ Site" />
        <FeatureRow icon={Database} label="ประเภท Asset" desc="กำหนดประเภทของ Asset ที่ใช้ใน Dropdown" />
        <FeatureRow icon={Database} label="สถานะ Asset" desc="กำหนดสถานะของ Asset ที่แสดงในระบบ" />
        <FeatureRow icon={Database} label="Brand / Vendor" desc="ยี่ห้ออุปกรณ์และผู้จำหน่าย" />
        <FeatureRow icon={Database} label="MA Templates" desc="แม่แบบการซ่อมบำรุงสำหรับ Asset แต่ละประเภท" />
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">แท็บ Import Asset (CSV)</h3>
      <StepList steps={[
        'ดาวน์โหลด Template CSV โดยคลิก "ดาวน์โหลด Template"',
        'กรอกข้อมูล Asset ใน Template ที่ดาวน์โหลด',
        'เลือกบริษัทที่ต้องการ Import เข้า',
        'ลาก CSV ลงใน Drop Zone หรือคลิกเพื่อเลือกไฟล์',
        'ตรวจสอบ Preview ข้อมูลที่จะ Import',
        'คลิก "Import" เพื่อนำเข้าข้อมูล — ระบบแจ้งผลสำเร็จ/ผิดพลาดรายแถว',
      ]} />

      <InfoBox title="หัวคอลัมน์ที่รองรับ (บางส่วน)" color="blue">
        <div className="flex flex-wrap gap-1.5 mt-1">
          {['เลขเครื่อง', 'ชื่อ', 'ประเภท', 'serial number', 'ยี่ห้อ', 'model', 'สถานะ', 'บริษัท', 'หน่วยงาน', 'ผู้ใช้', 'ip', 'ราคา', 'วันที่ซื้อ', 'วันหมดประกัน', 'หมายเหตุ'].map(h => (
            <code key={h} className="px-1.5 py-0.5 bg-white border border-blue-200 rounded text-xs font-mono text-blue-800">{h}</code>
          ))}
        </div>
      </InfoBox>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5">แท็บ System — Azure AD Sync</h3>
      <StepList steps={[
        'ไปที่ Settings → System → กด Expand บริษัทที่ต้องการ',
        'กรอก Tenant ID, Client ID และ Client Secret จาก Azure Portal',
        'คลิก "บันทึก" แล้วคลิก "ทดสอบ" เพื่อตรวจสอบการเชื่อมต่อ',
        'เปิด "ซิงค์อัตโนมัติ" และตั้งรอบการทำงาน (1 ชม. ถึง 1 สัปดาห์)',
        'คลิก "ซิงค์ตอนนี้" เพื่อดึง User จาก Azure AD ทันที',
      ]} />
      <InfoBox title="สิทธิ์ Azure App Registration ที่ต้องการ" color="amber">
        <p>ต้องให้สิทธิ์ <strong>User.Read.All</strong> แบบ Application Permission และ Admin Grant ใน Azure Portal</p>
      </InfoBox>
    </div>
  )
}

function SsoSection() {
  return (
    <div>
      <SectionTitle icon={KeyRound} title="SAML SSO (Microsoft)" subtitle="เข้าสู่ระบบด้วยบัญชี Microsoft — เป็นทางเลือกเสริมจาก username/password" />

      <p className="text-sm text-gray-700 leading-relaxed">
        ระบบรองรับการเข้าสู่ระบบผ่าน SAML SSO กับ Microsoft Entra ID (Azure AD) เพิ่มเติมจากช่องทาง
        username/password เดิม โดยตั้งค่าแยกได้ต่อบริษัท (Company) ในหน้า Settings → System
      </p>

      <InfoBox title="ข้อสำคัญ" color="amber">
        <p>• ผู้ใช้ต้อง <strong>มีบัญชีอยู่ในระบบนี้ก่อนแล้ว</strong> เท่านั้น — ระบบจะไม่สร้าง user ใหม่ให้อัตโนมัติเมื่อเข้าสู่ระบบผ่าน SSO</p>
        <p>• การจับคู่ผู้ใช้ทำโดยเทียบ <strong>อีเมล</strong> ระหว่างบัญชีในระบบกับอีเมลที่ได้จาก Microsoft — ต้องตรงกัน (ไม่สนตัวพิมพ์เล็ก/ใหญ่)</p>
        <p>• หาก user ถูกปิดใช้งาน (isActive = false) จะเข้าสู่ระบบผ่าน SSO ไม่ได้เช่นกัน</p>
      </InfoBox>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-5 flex items-center gap-1.5">
        <LogIn size={14} className="text-gray-500"/> การใช้งานฝั่งผู้ใช้
      </h3>
      <p className="text-sm text-gray-600">
        เมื่อ Admin เปิดใช้งาน SSO ให้บริษัทใดแล้ว หน้า Login จะมีปุ่ม
        <span className="font-medium text-gray-800"> "เข้าสู่ระบบด้วย Microsoft" </span>
        แสดงขึ้นเองใต้ฟอร์ม username/password — ผู้ใช้กดปุ่มนี้ ระบบจะพาไปหน้า Login ของ Microsoft
        เมื่อยืนยันตัวตนสำเร็จจะพากลับเข้าระบบทันทีโดยไม่ต้องกรอกรหัสผ่านของระบบเอง
      </p>

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-6 flex items-center gap-1.5">
        <Settings size={14} className="text-gray-500"/> การตั้งค่าฝั่ง Admin — ในระบบ
      </h3>
      <StepList steps={[
        'ไปที่ Settings → System → กด Expand บริษัทที่ต้องการ → แท็บ "SAML SSO"',
        'เปิดสวิตช์ "เปิดใช้งาน SAML SSO"',
        'คัดลอกค่า "Identifier (Entity ID)" และ "Reply URL (ACS URL)" ที่ระบบสร้างให้ไว้ก่อน — จะใช้ตั้งค่าฝั่ง Microsoft Entra ID ในขั้นตอนถัดไป',
        'กลับมากรอก IdP Entity ID, IdP SSO URL และ IdP Certificate ที่ได้จาก Microsoft Entra ID (ดูขั้นตอนด้านล่าง) แล้วกด "บันทึก"',
        'กดปุ่ม "ทดสอบเข้าสู่ระบบ" เพื่อลองยืนยันตัวตนแบบเต็มขั้นตอนก่อนใช้งานจริง',
      ]} />

      <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-6 flex items-center gap-1.5">
        <Link2 size={14} className="text-gray-500"/> การตั้งค่าฝั่ง Admin — ใน Microsoft Entra ID
      </h3>
      <StepList steps={[
        'เข้า Microsoft Entra admin center → Enterprise applications → All applications',
        'กด "Create your own application" ตั้งชื่อ (เช่น IT Management) แล้วเลือก "Integrate any other application you don\'t find in the gallery (Non-gallery)" → Create',
        'ในแอปที่สร้าง ไปที่เมนู Single sign-on → เลือก SAML',
        'ที่ "Basic SAML Configuration" กด Edit แล้วนำค่า Identifier (Entity ID) และ Reply URL (ACS URL) จากขั้นตอนก่อนหน้ามาใส่ → Save',
        'เลื่อนไปที่ "SAML Certificates" → กด Download ที่แถว "Certificate (Base64)" — จะได้ไฟล์ .cer',
        'เปิดไฟล์ .cer ด้วย Notepad แล้วคัดลอกข้อความทั้งหมด (รวมบรรทัด BEGIN/END CERTIFICATE)',
        'ที่กล่อง "Set up <ชื่อแอป>" คัดลอกค่า Login URL และ Microsoft Entra Identifier ด้วยไอคอน copy',
        'นำ Login URL, Microsoft Entra Identifier และ Certificate ที่คัดลอกไว้ กลับไปกรอกในระบบ (IdP SSO URL, IdP Entity ID, IdP Certificate ตามลำดับ)',
        'ที่เมนู Users and groups ของแอป เพิ่มผู้ใช้/กลุ่มที่ต้องการอนุญาตให้เข้าสู่ระบบผ่าน SSO ได้',
      ]} />

      <InfoBox title="ปัญหาที่พบบ่อย" color="blue">
        <p>• <strong>"เข้าสู่ระบบด้วย SSO ไม่สำเร็จ"</strong> ตอนกดปุ่มทดสอบ — มักเกิดจาก Certificate ที่วางไว้ไม่ตรงกับที่ Entra ใช้เซ็นจริง ลองดาวน์โหลด Certificate (Base64) ใหม่แล้ววางทับ</p>
        <p>• <strong>"ไม่พบบัญชีผู้ใช้ที่ตรงกับอีเมลนี้ในระบบ"</strong> — ตรวจสอบว่าอีเมลของ user ในเมนู ผู้ใช้งาน ตรงกับอีเมล Microsoft ของคนที่ล็อกอิน และบัญชีนั้น isActive อยู่</p>
        <p>• ปุ่ม "เข้าสู่ระบบด้วย Microsoft" ไม่แสดงที่หน้า Login — แปลว่ายังไม่มีบริษัทไหนตั้งค่า SSO ครบ (Entity ID + SSO URL + Certificate) และเปิดสวิตช์ใช้งานไว้</p>
      </InfoBox>

      <InfoBox title="ค่าที่ต้องใช้ (สรุป)" color="green">
        <div className="flex items-start gap-2">
          <Copy size={13} className="text-green-700 mt-0.5 flex-shrink-0"/>
          <p>จากระบบเรา → Identifier (Entity ID), Reply URL (ACS URL) — ใส่ใน Entra</p>
        </div>
        <div className="flex items-start gap-2 mt-1">
          <Copy size={13} className="text-green-700 mt-0.5 flex-shrink-0"/>
          <p>จาก Entra → Login URL, Microsoft Entra Identifier, Certificate (Base64) — ใส่ในระบบเรา</p>
        </div>
      </InfoBox>
    </div>
  )
}

function RolesSection() {
  return (
    <div>
      <SectionTitle icon={ShieldCheck} title="สิทธิ์การใช้งาน" subtitle="ความแตกต่างระหว่าง ADMIN และ USER" />
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase border border-gray-100">ฟีเจอร์</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase border border-gray-100">ADMIN</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase border border-gray-100">USER</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['ดูรายการ Asset',              true,  true  ],
              ['เพิ่ม / แก้ไข / ลบ Asset',   true,  false ],
              ['Export Excel',                 true,  true  ],
              ['ดู Software License',         true,  true  ],
              ['เพิ่ม / แก้ไข License',       true,  false ],
              ['ผูก / ยกเลิก Seat',           true,  false ],
              ['ดูใบส่งมอบ',                  true,  true  ],
              ['สร้าง / แก้ไขใบส่งมอบ',       true,  false ],
              ['แนบไฟล์ใบส่งมอบ',             true,  false ],
              ['สร้างรายการยืม',               true,  true  ],
              ['อนุมัติ / ปฏิเสธการยืม',      true,  false ],
              ['ดูรายงาน',                    true,  true  ],
              ['ตั้งค่า Master Data',          true,  false ],
              ['Import CSV',                  true,  false ],
              ['ตั้งค่า Azure AD Sync',        true,  false ],
              ['จัดการผู้ใช้งาน',             true,  false ],
            ].map(([feat, admin, user]) => (
              <tr key={feat as string} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-700 border border-gray-100">{feat as string}</td>
                <td className="px-3 py-2 text-center border border-gray-100">
                  {admin ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-400">—</span>}
                </td>
                <td className="px-3 py-2 text-center border border-gray-100">
                  {user ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

const CONTENT: Record<string, React.ReactNode> = {
  overview:   <OverviewSection   />,
  dashboard:  <DashboardSection  />,
  assets:     <AssetsSection     />,
  software:   <SoftwareSection   />,
  deliveries: <DeliveriesSection />,
  bookings:   <BookingsSection   />,
  reports:    <ReportsSection    />,
  settings:   <SettingsSection   />,
  sso:        <SsoSection        />,
  roles:      <RolesSection      />,
}

export default function ManualTab() {
  const [active, setActive] = useState('overview')

  return (
    <div className="flex gap-0 h-full min-h-0">
      {/* Sidebar nav */}
      <nav className="w-48 flex-shrink-0 border-r border-gray-100 py-4 overflow-y-auto">
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActive(id)}
            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors text-left ${
              active === id
                ? 'bg-gray-900 text-white font-medium'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <Icon size={14} className="flex-shrink-0" />
            <span className="leading-tight">{label}</span>
          </button>
        ))}
      </nav>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 max-w-2xl">
        {CONTENT[active]}
      </div>
    </div>
  )
}
